import React, { useState, useEffect } from "react";
import { auth, db } from "./firebase";
import { collection, addDoc, serverTimestamp, query, where, getDocs } from "firebase/firestore";
import { 
  User, Activity, Phone, MapPin, UserPlus, 
  Calendar, Ruler, PencilLine, ArrowRight, Scale, Heart, Thermometer, Stethoscope, Search, Info
} from "lucide-react";

const RegisterPatient = () => {
  const initialData = {
    name: "", dob: "", age: "", gender: "Male", phone: "",
    address: "", height: "", weight: "", 
    bloodPressure: "", temperature: "", pulse: "",
    reasonForVisit: "", notes: ""
  };

  const [formData, setFormData] = useState(initialData);
  const [loading, setLoading] = useState(false);
  const [existingPatients, setExistingPatients] = useState([]);
  const [showPopup, setShowPopup] = useState(false);

  // --- AGE CALCULATION LOGIC ---
  const handleDOBChange = (dobValue) => {
    if (!dobValue) return;

    const birthDate = new Date(dobValue);
    const today = new Date();
    
    let calculatedAge = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    // Check if birthday has occurred yet this year
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      calculatedAge--;
    }

    setFormData({
      ...formData,
      dob: dobValue,
      age: calculatedAge > 0 ? calculatedAge.toString() : "0"
    });
  };

  useEffect(() => {
    const searchPatients = async () => {
      const user = auth.currentUser;
      if (!user || formData.phone.length < 5) {
        setExistingPatients([]);
        setShowPopup(false);
        return;
      }

      try {
        const q = query(
          collection(db, "hospitals", user.uid, "patients"),
          where("phone", "==", formData.phone)
        );
        const querySnapshot = await getDocs(q);
        const patients = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        
        if (patients.length > 0) {
          setExistingPatients(patients);
          setShowPopup(true);
        } else {
          setShowPopup(false);
        }
      } catch (err) {
        console.error("Search error:", err);
      }
    };

    const delayDebounce = setTimeout(() => {
      searchPatients();
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [formData.phone]);

  const selectExistingPatient = (p) => {
    setFormData({
      ...formData,
      name: p.name || "",
      age: p.age || "",
      gender: p.gender || "Male",
      address: p.address || "",
      dob: p.dob || ""
    });
    setShowPopup(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = auth.currentUser;
      if (!user) throw new Error("No user logged in");

      // --- NEW: DAILY QUEUE NUMBER LOGIC ---
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // Innaiku register aana patients-ah mattum count panna query
      const q = query(
        collection(db, "hospitals", user.uid, "patients"),
        where("registeredAt", ">=", today)
      );
      
      const querySnapshot = await getDocs(q);
      const queueNo = querySnapshot.size + 1; // Innaiku count + 1 = Next Token

      const patientId = `P-${Math.floor(1000 + Math.random() * 9000)}`;

      await addDoc(collection(db, "hospitals", user.uid, "patients"), {
        ...formData,
        patientId,
        queueNo, // Intha number thaan ovvoru naalum 1-la irunthu start aagum
        status: "waiting",
        registeredAt: serverTimestamp(),
        visitHistory: []
      });

      alert(`Patient Registered! Token No: ${queueNo}`);
      setFormData(initialData);
    } catch (err) {
      console.error(err);
      alert("Error saving data");
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="min-h-screen bg-[#F1F5F9] p-4 md:p-10 font-sans selection:bg-indigo-100 relative">
      <div className="max-w-5xl mx-auto">
        
        <div className="mb-10 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-black text-slate-800 flex items-center gap-3">
              <div className="p-2.5 bg-indigo-600 rounded-2xl shadow-lg shadow-indigo-200">
                <UserPlus className="text-white" size={28} />
              </div>
              Patient Registration
            </h1>
            <p className="text-slate-500 font-medium mt-1 ml-14">Enroll a new patient into the hospital system.</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          
          <div className="bg-white rounded-[2.5rem] p-8 shadow-sm border border-white relative">
            <div className="flex items-center gap-2 mb-8 text-indigo-600">
              <User size={20} className="font-bold" />
              <h2 className="text-sm font-black uppercase tracking-widest">Personal Information</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="relative">
                <InputGroup label="Mobile Number" icon={<Phone size={18}/>}>
                  <input 
                    required 
                    type="tel" 
                    placeholder="10-digit number" 
                    value={formData.phone} 
                    onChange={(e) => setFormData({...formData, phone: e.target.value})} 
                    className="input-style border-indigo-200 bg-indigo-50/30" 
                  />
                </InputGroup>

                {showPopup && (
                  <div className="absolute z-50 w-full mt-2 bg-white rounded-2xl shadow-2xl border border-slate-100 overflow-hidden">
                    <div className="bg-indigo-600 p-3 flex items-center gap-2">
                      <Info size={14} className="text-white" />
                      <p className="text-[10px] font-black text-white uppercase tracking-wider">Existing Patients Found</p>
                    </div>
                    <div className="max-h-48 overflow-y-auto">
                      {existingPatients.map((p, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => selectExistingPatient(p)}
                          className="w-full p-4 text-left hover:bg-indigo-50 border-b border-slate-50 last:border-0 transition-colors flex justify-between items-center group"
                        >
                          <div>
                            <p className="text-sm font-black text-slate-800 group-hover:text-indigo-600">{p.name}</p>
                            <p className="text-[10px] text-slate-400 font-bold uppercase">{p.age} Yrs • {p.gender}</p>
                          </div>
                          <ArrowRight size={14} className="text-slate-300 group-hover:text-indigo-600" />
                        </button>
                      ))}
                    </div>
                    <button type="button" onClick={() => setShowPopup(false)} className="w-full p-2 text-[10px] font-bold text-slate-400 hover:bg-slate-50 uppercase">Close</button>
                  </div>
                )}
              </div>

              <InputGroup label="Full Name" icon={<User size={18}/>}>
                <input required placeholder="Enter name" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="input-style" />
              </InputGroup>

              <div className="grid grid-cols-2 gap-4">
                <InputGroup label="Age">
                  <input required type="number" placeholder="Age" value={formData.age} onChange={(e) => setFormData({...formData, age: e.target.value})} className="input-style" />
                </InputGroup>
                <InputGroup label="Gender">
                  <select value={formData.gender} onChange={(e) => setFormData({...formData, gender: e.target.value})} className="input-style bg-slate-50">
                    <option>Male</option><option>Female</option><option>Other</option>
                  </select>
                </InputGroup>
              </div>

              <div className="md:col-span-1">
                <InputGroup label="Date of Birth" icon={<Calendar size={18}/>}>
                  <input 
                    type="date" 
                    max={new Date().toISOString().split("T")[0]} // Prevents future dates
                    value={formData.dob} 
                    onChange={(e) => handleDOBChange(e.target.value)} 
                    className="input-style" 
                  />
                </InputGroup>
              </div>

              <div className="md:col-span-2">
                <InputGroup label="Residential Address" icon={<MapPin size={18}/>}>
                  <input placeholder="Street, City, Area" value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} className="input-style" />
                </InputGroup>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-[2.5rem] p-8 shadow-sm border border-white">
            <div className="flex items-center gap-2 mb-8 text-rose-500">
              <Activity size={20} />
              <h2 className="text-sm font-black uppercase tracking-widest">Vital Signs (Initial)</h2>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <InputGroup label="Height (cm)" icon={<Ruler size={16}/>}>
                <input placeholder="170" value={formData.height} onChange={(e) => setFormData({...formData, height: e.target.value})} className="input-style" />
              </InputGroup>
              <InputGroup label="Weight (kg)" icon={<Scale size={16}/>}>
                <input placeholder="65" value={formData.weight} onChange={(e) => setFormData({...formData, weight: e.target.value})} className="input-style" />
              </InputGroup>
              <InputGroup label="B.P (mmHg)" icon={<Heart size={16}/>}>
                <input placeholder="120/80" value={formData.bloodPressure} onChange={(e) => setFormData({...formData, bloodPressure: e.target.value})} className="input-style" />
              </InputGroup>
              <InputGroup label="Temp (°F)" icon={<Thermometer size={16}/>}>
                <input placeholder="98.6" value={formData.temperature} onChange={(e) => setFormData({...formData, temperature: e.target.value})} className="input-style" />
              </InputGroup>
            </div>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
              <InputGroup label="Reason for Visit" icon={<Stethoscope size={18}/>}>
                <textarea rows="3" placeholder="Chief complaints..." value={formData.reasonForVisit} onChange={(e) => setFormData({...formData, reasonForVisit: e.target.value})} className="input-style resize-none" />
              </InputGroup>
              <InputGroup label="Clinical Notes (Optional)" icon={<PencilLine size={18}/>}>
                <textarea rows="3" placeholder="Additional observations..." value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})} className="input-style resize-none" />
              </InputGroup>
            </div>
          </div>

          <div className="flex items-center justify-end gap-4 pb-10">
            <button type="button" onClick={() => setFormData(initialData)} className="px-8 py-4 font-bold text-slate-500 hover:text-slate-800 transition-colors">Reset</button>
            <button 
              disabled={loading}
              type="submit" 
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-12 py-4 rounded-[1.5rem] font-bold shadow-xl flex items-center gap-3 active:scale-95 disabled:opacity-50"
            >
              {loading ? "Registering..." : "Complete Registration"}
              <ArrowRight size={20} />
            </button>
          </div>
        </form>
      </div>

      <style>{`
        .input-style {
          width: 100%;
          padding: 0.875rem 1rem;
          background-color: #F8FAFC;
          border: 1px solid #E2E8F0;
          border-radius: 1rem;
          font-weight: 600;
          font-size: 0.875rem;
          color: #1E293B;
          outline: none;
          transition: all 0.2s;
        }
        .input-style:focus {
          border-color: #6366F1;
          background-color: #FFFFFF;
          box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.05);
        }
      `}</style>
    </div>
  );
};

const InputGroup = ({ label, icon, children }) => (
  <div className="space-y-2">
    <label className="text-[11px] font-black text-slate-400 uppercase tracking-wider ml-1 flex items-center gap-1.5">
      {icon} {label}
    </label>
    {children}
  </div>
);

export default RegisterPatient;